import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Backdrop,
  Box,
  Button,
  Dialog,
  IconButton,
  Tooltip,
  Typography,
  CircularProgress,
  makeStyles,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import moment from "moment";
import * as api from "../forecastApi";
import FilterBar from "./FilterBar";
import ForecastTable from "./ForecastTable";
import ForecastHistoryDialog from "./ForecastHistoryDialog";
import ApproveForecastDialog from "./ApproveForecastDialog";
import DownloadPreviewDialog from "./DownloadPreviewDialog";
import ForecastSuccessModal from "./ForecastSuccessModal";
import ForecastChat from "../ForecastChat";
import StyledDialog from "../../common/updateGrRecord/Dialog";
import jwtDecode from "jwt-decode";

const useStyles = makeStyles((theme) => ({
  root: {
    // backgroundColor: "#f5f5f5",
    // minHeight: "calc(100vh - 64px)",
  },
  topMeta: {
    display: "flex",
    alignItems: "end",
    justifyContent: "flex-end",
    padding: theme.spacing(1, 3, 1, 3),
    flexDirection: 'column'
  },
  lastSyncedText: {
    color: "#666",
    fontSize: "0.85rem",
  },
  refreshLink: {
    color: "#ec6535",
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    fontSize: "0.85rem",
    fontStyle: "italic",
    "&:hover": { color: "#9E4D27" },
  },
  perPageBox: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  perPageLabel: {
    fontSize: "0.8rem",
    color: "#666",
    letterSpacing: "0.05em",
  },
  perPageSelect: {
    minWidth: 60,
  },
  section: {
    padding: theme.spacing(2, 3),
    backgroundColor: "#fff",
    // margin: theme.spacing(2, 3, 3, 3),
    borderRadius: 4,
    border: "1px solid #e0e0e0",
  },
  sectionHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: theme.spacing(2),
    flexWrap: "wrap",
    gap: theme.spacing(1),
  },
  sectionTitle: {
    fontWeight: 700,
    fontSize: "1.2rem",
  },
  buttonRow: {
    display: "flex",
    gap: theme.spacing(1),
    flexWrap: "wrap",
  },
  outlineBtn: {
    backgroundColor: "#fff",
    color: "#212121",
    border: "1px solid #c4c4c4",
    textTransform: "none",
    boxShadow: "none",
    borderRadius: 4,
    padding: "6px 14px",
    "&:hover": { backgroundColor: "#f8f8f8", boxShadow: "none" },
    "&.Mui-disabled": { color: "#aaa" },
  },
  approveBtn: {
    backgroundColor: "#1976d2",
    color: "#fff",
    textTransform: "none",
    boxShadow: "none",
    borderRadius: 4,
    padding: "6px 16px",
    "&:hover": { backgroundColor: "#0d47a1", boxShadow: "none" },
    "&.Mui-disabled": { backgroundColor: "#90caf9", color: "#fff" },
  },
  cancelEditBtn: {
    color: "#e53935",
    border: "1px solid #e53935",
    padding: 4,
    marginLeft: theme.spacing(0.5),
    "&:hover": { backgroundColor: "#fdecea" },
  },
  backdrop: {
    zIndex: theme.zIndex.modal + 100,
    color: "#fff",
    flexDirection: "column",
    gap: theme.spacing(2),
  },
}));

const firstOfMonth = (m) => m.clone().startOf("month").format("YYYY-MM-DD");

const getTokenData = () => {
  try {
    const token = localStorage.getItem("authToken");
    if (!token) return {};
    return jwtDecode(token) || {};
  } catch (e) {
    return {};
  }
};

const getInitialFilters = () => {
  const td = getTokenData();
  const isCMO = localStorage.getItem("collabType") === "CMO";
  return {
    cmoOrgID: isCMO ? td?.organization_id : null,
    cmoLabel: "",
    sponsorOrgID: !isCMO ? td?.organization_id : null,
    sponsorLabel: "",
    brand: "",
    aslNumber: null,
    startMonth: firstOfMonth(moment()),
    endMonth: firstOfMonth(moment().add(12, "months")),
    pageNumber: 1,
    pageSize: 10,
  };
};

const buildFetchPayload = (filters, overrides = {}) => {
  return {
    cmoOrgID: filters.cmoOrgID,
    sponsorOrgID: filters.sponsorOrgID,
    collabrixForecastSyncID: 0,
    brand: filters.brand,
    aslNumber: filters.aslNumber ? filters.aslNumber : null,
    startMonth: filters.startMonth,
    endMonth: filters.endMonth,
    pageNumber: filters.pageNumber || 1,
    pageSize: filters.pageSize || 10,
    ...overrides,
  };
};

export default function ForecastDashboard() {
  const classes = useStyles();
  const sponsor = (localStorage.getItem("collabType") || "") === "Pharma Co";
  const tokenData = useMemo(getTokenData, []);

  // ---- Filters & forecast data ----
  const [filters, setFilters] = useState(getInitialFilters);
  const [forecastList, setForecastList] = useState([]);
  const [pagination, setPagination] = useState({
    totalNoOfRecords: 0,
    noOfPages: 0,
  });
  const [hasFetched, setHasFetched] = useState(false);
  const [dataLoading, setDataLoading] = useState(false);
  const [lastSyncTime, setLastSyncTime] = useState(new Date());

  // ---- Edit mode ----
  const [editMode, setEditMode] = useState(false);
  const [editBuffer, setEditBuffer] = useState({});
  const [newCellsMeta, setNewCellsMeta] = useState({});
  const [saveLoading, setSaveLoading] = useState(false);

  // ---- History dialog ----
  const [historyOpen, setHistoryOpen] = useState(false);

  // ---- Approve dialog ----
  const [approveOpen, setApproveOpen] = useState(false);
  const [approveLoading, setApproveLoading] = useState(false);
  const [approveInfo, setApproveInfo] = useState(null);

  // ---- Success modal & misc ----
  const [successOpen, setSuccessOpen] = useState(false);
  const [successText, setSuccessText] = useState("");
  const [successInfo, setSuccessInfo] = useState(null);
  const [downloadInProgress, setDownloadInProgress] = useState(false);
  const [downloadPreviewOpen, setDownloadPreviewOpen] = useState(false);
  const [downloadPreviewRows, setDownloadPreviewRows] = useState([]);
  const [error, setError] = useState("");


  // ---- Hydrate filters once from saved user filters ----
  useEffect(() => {
    const ctrl = new AbortController();
    api
      .fetchAllFilters(ctrl.signal)
      .then((res) => {
        if (res && res.status && res.data && res.data.forecastDashboard) {
          setFilters((prev) => {
            const saved = res.data.forecastDashboard;
            const isCMO = localStorage.getItem("collabType") === "CMO";
            return {
              ...prev,
              ...saved,
              // Own org ID always comes from the JWT token, never from saved filters
              ...(isCMO ? { cmoOrgID: prev.cmoOrgID } : { sponsorOrgID: prev.sponsorOrgID }),
            };
          });
        }
      })
      .catch(() => { });
    return () => ctrl.abort();
  }, []);

  // ---- Filter mutation helpers (passed to children) ----
  const updateFilters = (patch) =>
    setFilters((prev) => ({ ...prev, ...patch, pageNumber: 1 }));

  const updateFiltersRaw = (patch) =>
    setFilters((prev) => ({ ...prev, ...patch }));

  const clearFilters = () => {
    setFilters(getInitialFilters());
    setForecastList([]);
    setPagination({ totalNoOfRecords: 0, noOfPages: 0 });
    setHasFetched(false);
    setEditMode(false);
    setEditBuffer({});
    setNewCellsMeta({});
  };

  // ---- Load forecasts ----
  const loadForecasts = useCallback(
    async (overrideFilters) => {
      console.log(filters)
      const f = overrideFilters || filters;
      setDataLoading(true);
      setError("");
      try {
        const res = await api.fetchForecasts(buildFetchPayload(f));
        if (res && res.status) {
          const data = res.data || {};
          setForecastList(data.forecastDetailsList || []);
          setLastSyncTime(new Date());
          setPagination({
            totalNoOfRecords: data.totalNoOfRecords || 0,
            noOfPages: data.noOfPages || 0,
          });
          setHasFetched(true);
          setEditBuffer({});
          setNewCellsMeta({});
          setEditMode(false);
        } else {
          setForecastList([]);
          setPagination({ totalNoOfRecords: 0, noOfPages: 0 });
          setError(
            (res && (res.errorDescription || res.message)) ||
            "Failed to load forecasts"
          );
        }
      } catch (e) {
        setError("Failed to load forecasts");
      } finally {
        setDataLoading(false);
      }
    },
    [filters]
  );

  // ---- Save forecast filters ----
  const [filtersSaveLoading, setFiltersSaveLoading] = useState(false);
  const saveCurrentFilters = async () => {
    setFiltersSaveLoading(true);
    try {
      await api.saveFilters({
        cmoOrgID: filters.cmoOrgID,
        cmoLabel: filters.cmoLabel,
        sponsorOrgID: filters.sponsorOrgID,
        sponsorLabel: filters.sponsorLabel,
        brand: filters.brand,
        aslNumber: filters.aslNumber,
        startMonth: filters.startMonth,
        endMonth: filters.endMonth,
      });
      setSuccessText("Filters saved");
      setSuccessInfo(null);
      setSuccessOpen(true);
    } catch (e) {
      setError("Failed to save filters");
    } finally {
      setFiltersSaveLoading(false);
    }
  };

  // ---- Edit / Save quantities ----
  const onEdit = () => setEditMode(true);

  const onCancelEdit = () => {
    if (saveLoading) return;
    setEditBuffer({});
    setNewCellsMeta({});
    setEditMode(false);
  };

  const onCellChange = (id, quantity, meta) => {
    setEditBuffer((prev) => ({ ...prev, [id]: quantity }));
    if (meta) setNewCellsMeta((prev) => ({ ...prev, [id]: meta }));
  };

  const onSave = async () => {
    const changedIds = Object.keys(editBuffer);
    if (changedIds.length === 0) {
      setEditMode(false);
      return;
    }
    const byKey = new Map(
      forecastList.map((r) => [`${r.collabrixProductID}__${r.forecastMonth}`, r])
    );
    const updates = changedIds
      .filter((id) => !id.startsWith("new__") && byKey.has(id))
      .reduce((acc, id) => {
        const orig = byKey.get(id);
        const newQty = Number(editBuffer[id]);
        if (newQty !== orig.quantity) {
          acc.push({
            collabrixForecastDetailID: orig.collabrixForecastDetailID,
            collabrixForecastSyncID: orig.collabrixForecastSyncID,
            collabrixProductID: orig.collabrixProductID,
            quantity: newQty,
            sponsorOrgID: orig.sponsorOrgID,
            cmoOrgID: orig.cmoOrgID,
          });
        }
        return acc;
      }, []);

    const creates = changedIds
      .filter((id) => id.startsWith("new__") && newCellsMeta[id] && Number(editBuffer[id]) > 0)
      .map((id) => {
        const meta = newCellsMeta[id];
        return {
          collabrixForecastDetailID: 0,
          collabrixForecastSyncID: meta.collabrixForecastSyncID,
          collabrixProductID: meta.collabrixProductID,
          forecastMonth: meta.forecastMonth,
          quantity: Number(editBuffer[id]),
          sponsorOrgID: meta.sponsorOrgID,
          cmoOrgID: meta.cmoOrgID,
        };
      });

    if (updates.length === 0 && creates.length === 0) {
      setEditMode(false);
      return;
    }

    setSaveLoading(true);
    try {
      const res = await api.updateForecastDetails({
        forecastDetailsList: [...updates, ...creates],
      });
      if (res && res.status) {
        setSaveLoading(false);
        setEditMode(false);
        setEditBuffer({});
        setNewCellsMeta({});
        setSuccessText("Forecast Saved Successfully");
        setSuccessInfo({
          cmoLabel: filters.cmoLabel,
          brand: filters.brand,
          receivedOn: moment(filters.startMonth, "YYYY-MM-DD").format(
            "MMM YYYY"
          ),
        });
        setSuccessOpen(true);
        await loadForecasts();
      } else {
        setSaveLoading(false);
        setError(
          (res && (res.errorDescription || res.message)) ||
          "Failed to save forecast"
        );
      }
    } catch (e) {
      setSaveLoading(false);
      setError("Failed to save forecast");
    }
  };

  // ---- History ----
  const onOpenHistory = () => setHistoryOpen(true);
  const onCloseHistory = () => setHistoryOpen(false);

  // ---- Approve ----
  const onApproveClick = () => {
    setApproveInfo({
      cmoLabel: filters.cmoLabel,
      brand: filters.brand,
      receivedOn: moment(filters.startMonth, "YYYY-MM-DD").format("MMM YYYY"),
    });
    setApproveLoading(false);
    setApproveOpen(true);
  };

  const onApproveConfirm = async () => {
    setApproveOpen(false);
    setApproveLoading(true);
    try {
      await api.approveForecast();
      setApproveLoading(false);
      setSuccessText("Forecast Approved Successfully");
      setSuccessInfo(approveInfo);
      setSuccessOpen(true);
    } catch (e) {
      setApproveLoading(false);
      setError(e.message || "Something went wrong");
    }
  };

  const onApproveCancel = () => {
    if (approveLoading) return;
    setApproveOpen(false);
  };

  // ---- Download (preview first, then download) ----
  const onDownloadMonthly = async () => {
    setDownloadInProgress(true);
    setDownloadPreviewRows([]);
    setDownloadPreviewOpen(true);
    try {
      const res = await api.fetchForecasts(
        buildFetchPayload(filters, {
          collabrixForecastSyncID: 0,
          pageNumber: 0,
          pageSize: 0,
        })
      );
      const rows = (res && res.data && res.data.forecastDetailsList) || [];
      setDownloadPreviewRows(rows);
    } catch (e) {
      setError("Failed to load preview");
      setDownloadPreviewOpen(false);
    } finally {
      setDownloadInProgress(false);
    }
  };

  // ---- Per-page change re-fetches with the new page size ----
  const onPerPageChange = (e) => {
    const size = parseInt(e.target.value, 10);
    const next = { ...filters, pageSize: size, pageNumber: 1 };
    setFilters(next);
    if(hasFetched){
      loadForecasts(next);
    }
  };

  const goPage = (p) => {
    if (p < 1 || p > (pagination.noOfPages || 1)) return;
    const next = { ...filters, pageNumber: p };
    setFilters(next);
    loadForecasts(next);
  };

  const tableVisible = hasFetched;
  const isEmpty = hasFetched && forecastList.length === 0;

  return (
    <Box className={classes.root}>
      <FilterBar
        tokenData={tokenData}
        filters={filters}
        sponsor={sponsor}
        editMode={editMode}
        filtersSaveLoading={filtersSaveLoading}
        updateFilters={updateFilters}
        updateFiltersRaw={updateFiltersRaw}
        clearFilters={clearFilters}
        onSaveFilters={saveCurrentFilters}
        onViewReports={() => loadForecasts()}
        showRefresh={tableVisible}
        lastSyncTime={lastSyncTime}
        dataLoading={dataLoading}
        onRefresh={() => loadForecasts()}
        onPerPageChange={onPerPageChange}
      />

      {!tableVisible && dataLoading && (
        <Box style={{ display: "flex", justifyContent: "center", padding: "48px 0" }}>
          <CircularProgress />
        </Box>
      )}

      {tableVisible && (
        <Box className={classes.section}>
          <Box className={classes.sectionHeader}>
            <Typography className={classes.sectionTitle}>
              Forecast Dashboard
            </Typography>
            <Box className={classes.buttonRow}>
              {sponsor && !editMode && (
                <Button className={classes.outlineBtn} onClick={onEdit} disabled={isEmpty}>
                  Edit forecast
                </Button>
              )}
              {sponsor && editMode && (
                <>
                  <Button
                    className={classes.outlineBtn}
                    onClick={onSave}
                    disabled={saveLoading}
                  >
                    Save changes
                  </Button>
                  <Tooltip title="Cancel edit">
                    <IconButton
                      size="small"
                      className={classes.cancelEditBtn}
                      onClick={onCancelEdit}
                      disabled={saveLoading}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </>
              )}
              <Button
                className={classes.outlineBtn}
                onClick={onOpenHistory}
                disabled={isEmpty || editMode}
              >
                View forecast history
              </Button>
              <Button
                className={classes.outlineBtn}
                onClick={onDownloadMonthly}
                disabled={isEmpty || downloadInProgress || editMode}
                startIcon={
                  downloadInProgress ? <CircularProgress size={14} /> : null
                }
              >
                Download monthly report
              </Button>
              {sponsor && (
                <Button
                  className={classes.approveBtn}
                  onClick={onApproveClick}
                  disabled={isEmpty || editMode}
                >
                  Approve forecast
                </Button>
              )}

            </Box>
          </Box>

          <ForecastTable
            forecastList={forecastList}
            dataLoading={dataLoading}
            editMode={editMode}
            editBuffer={editBuffer}
            onCellChange={onCellChange}
            filters={filters}
            pagination={pagination}
            goPage={goPage}
          />
        </Box>
      )}

      {tableVisible && forecastList.length > 0 && (
        <ForecastChat hasFetched={hasFetched} forecastList={forecastList} filters={filters} />
      )}

      <ForecastHistoryDialog
        open={historyOpen}
        filters={filters}
        onClose={onCloseHistory}
      />
      <ApproveForecastDialog
        open={approveOpen}
        loading={approveLoading}
        info={approveInfo}
        onConfirm={onApproveConfirm}
        onCancel={onApproveCancel}
      />
      <DownloadPreviewDialog
        open={downloadPreviewOpen}
        loading={downloadInProgress}
        rows={downloadPreviewRows}
        cmoLabel={!sponsor ? filters.sponsorLabel : filters.cmoLabel}
        brand={filters.brand}
        monthRange={{ start: filters.startMonth, end: filters.endMonth }}
        columnHeader={!sponsor ? "Sponsor" : "CMO"}
        onClose={() => setDownloadPreviewOpen(false)}
      />
      <ForecastSuccessModal
        open={successOpen}
        text={successText}
        info={successInfo}
        onClose={() => {
          setSuccessOpen(false);
          setSuccessText("");
          setSuccessInfo(null);
        }}
      />
      <Backdrop
        className={classes.backdrop}
        open={saveLoading || approveLoading}
      >
        <CircularProgress color="inherit" />
        <Typography variant="body2">
          {saveLoading ? "Saving forecast..." : "Approving forecast..."}
        </Typography>
      </Backdrop>
      <Dialog open={!!error} onClose={() => setError("")} fullWidth>
        <StyledDialog
          icon={true}
          title="Something Went Wrong. Please try again."
          // subtitle={error}
          handleClose={() => setError("")}
        />
      </Dialog>
    </Box>
  );
}
